import { Input } from '@sjuni/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import { redirectTarget } from '@/lib/auth/redirectTarget';
import { useAuth } from '@/lib/auth/useAuth';

/**
 * docs/04-API-SPEC.md §5 — `POST /admin/auth/two-factor-challenge`.
 *
 * Reached holding the challenge token, which can do nothing else and lives ten
 * minutes. Both credentials the endpoint accepts are offered: a phone is not
 * always to hand, and a recovery code that nobody can find the field for is the
 * same as no recovery code.
 */
interface ChallengeFields {
  code: string;
  recovery_code: string;
}

export function TwoFactorChallengePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, completeChallenge } = useAuth();

  const [usingRecovery, setUsingRecovery] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChallengeFields>({ defaultValues: { code: '', recovery_code: '' } });

  // Landing here without the token — a refresh after it expired, or a guessed
  // URL — means starting again.
  if (session?.status !== 'two_factor_required') {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await completeChallenge(
        usingRecovery ? { recovery_code: values.recovery_code } : { code: values.code },
      );

      navigate(redirectTarget(location), { replace: true });
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'code',
        'recovery_code',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      <FormError message={formError} />

      {usingRecovery ? (
        <Input
          {...register('recovery_code', { required: t('auth.validation.recoveryRequired') })}
          autoComplete="one-time-code"
          /* eslint-disable-next-line jsx-a11y/no-autofocus -- a screen whose only
             purpose is this one form; the rule guards against stealing focus
             mid-page, which is not what this is. */
          autoFocus
          label={t('auth.fields.recoveryCode')}
          hint={t('auth.twoFactor.recoveryHint')}
          error={errors.recovery_code?.message}
          required
        />
      ) : (
        <Input
          {...register('code', { required: t('auth.validation.codeRequired') })}
          /*
           * `inputMode` rather than `type="number"`: a TOTP code is six digits
           * that may start with a zero, and a number input drops it.
           */
          inputMode="numeric"
          autoComplete="one-time-code"
          /* eslint-disable-next-line jsx-a11y/no-autofocus -- a screen whose only
             purpose is this one form; the rule guards against stealing focus
             mid-page, which is not what this is. */
          autoFocus
          label={t('auth.fields.code')}
          hint={t('auth.twoFactor.codeHint')}
          error={errors.code?.message}
          required
        />
      )}

      <SubmitButton fullWidth loading={isSubmitting}>
        {t('auth.twoFactor.submit')}
      </SubmitButton>

      <p className="text-center text-body-sm">
        <button
          type="button"
          onClick={() => setUsingRecovery((current) => !current)}
          className="text-link underline-offset-2 hover:underline"
        >
          {usingRecovery ? t('auth.twoFactor.useCode') : t('auth.twoFactor.useRecovery')}
        </button>
      </p>
    </form>
  );
}
